<template>
	<div class="mission-runner-container">
		<Header :title="mission?.name || 'Mission Runner'" :user="currentUser" />

		<main class="page-main" v-if="mission">
			<div class="mission-info">
				<h2>{{ mission.name }}</h2>
				<!-- show big countdown timer (remaining time) here instead of Total Time -->
				<div class="current-time">{{ formatTime(remainingTime) }}</div>
			</div>

			<div class="runner-controls">
				<div class="timer-display">
					<div class="progress-bar">
						<div 
							class="progress-fill" 
							:style="{ width: progressPercentage + '%' }"
						></div>
					</div>
				</div>
				<div class="current-step-display">
					<div class="current-step-name">{{ mission.steps[currentStepIndex]?.name || '—' }}</div>
					<div class="current-step-elapsed">{{ formatTime(perStepElapsed[currentStepIndex] || 0) }}</div>
				</div>

				<div class="action-btn-bar">
					<button 
						v-if="!isRunning && !isCompleted" 
						@click="startMission" 
						class="construct-btn action-btn"
					>
						Start
					</button>
					<button
						v-if="isRunning && !isCompleted"
						@click="markStepDone"
						class="action-btn construct-btn"
					>
						Done
					</button>
					<button
						v-if="isRunning && !isCompleted"
						@click="skipStep"
						class="action-btn"
					>
						Skip
					</button>
					<button
						v-if="isRunning && !isCompleted"
						@click="pauseMission"
						class="action-btn"
					>
						Pause
					</button>
					<button
						v-if="!isRunning && !isCompleted && currentTime > 0"
						@click="stopMission"
						class="action-btn destruct-btn"
					>
						Stop
					</button>
					<button 
						v-if="!isRunning && currentTime > 0 && !isCompleted" 
						@click="resumeMission" 
						class="action-btn construct-btn"
					>
						Resume
					</button>
				</div>
			</div>

			<div class="steps-section">
				<h3>Steps</h3>
				<div v-if="mission.steps.length === 0" class="no-steps">
					No steps defined for this mission
				</div>
				<div v-else class="steps-list">
					<div
						v-for="(step, index) in mission.steps"
						:key="step.id"
						class="step-item"
						:class="{
							'step-current': index === currentStepIndex,
							'step-completed': index < currentStepIndex,
							'step-upcoming': index > currentStepIndex
						}"
					>
						<div class="step-number">{{ index + 1 }}</div>
						<div class="step-content">
							<h4>{{ step.name }}</h4>
								<div class="step-elapsed">Elapsed: {{ formatTime(perStepElapsed[index] || 0) }}</div>
						</div>
						<div class="step-status">
							<span v-if="index < currentStepIndex" class="status-completed">Completed</span>
							<span v-else-if="index === currentStepIndex" class="status-current">Current</span>
							<span v-else class="status-upcoming">Upcoming</span>
						</div>
					</div>
				</div>
			</div>

			<div v-if="isCompleted" class="completion-message">
				<h3>Mission Completed!</h3>
				<p>Great job! You have completed all steps of this mission.</p>
			</div>
		</main>

		<div v-else-if="loading" class="loading">
			Loading mission...
		</div>

		<div v-else class="error">
			Mission not found
		</div>
	</div>
</template>

<script setup lang="ts">
// Skip current step and move to next
const skipStep = () => {
	if (!mission.value) return
	if (currentStepIndex.value < mission.value.steps.length - 1) {
		perStepStart.value[currentStepIndex.value + 1] = Date.now()
		currentStepIndex.value++
	} else {
		completeMission()
	}
}

// Pause mission timer
const pauseMission = () => {
	isRunning.value = false
	if (intervalId) {
		clearInterval(intervalId)
		intervalId = null
	}
	releaseWakeLock()
	stopSpeechLoop()
}

// Stop mission and reset all progress
const stopMission = () => {
	if (typeof window !== 'undefined') {
		const ok = window.confirm('Stop this mission and reset your progress?')
		if (!ok) return
	}
	isRunning.value = false
	isCompleted.value = false
	currentTime.value = 0
	currentStepIndex.value = 0
	perStepElapsed.value = Array(mission.value?.steps.length || 0).fill(0)
	perStepStart.value = Array(mission.value?.steps.length || 0).fill(null)
	if (intervalId) {
		clearInterval(intervalId)
		intervalId = null
	}
	releaseWakeLock()
	stopSpeechLoop()
}
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import Header from '../components/Header.vue'
import { API_BASE_URL, dbService, type Mission } from '../services/database'

const route = useRoute()
const mission = ref<Mission | null>(null)
const loading = ref(false)
const isRunning = ref(false)
const currentTime = ref(0)
const currentStepIndex = ref(0)
const isCompleted = ref(false)
let intervalId: number | null = null
const perStepElapsed = ref<number[]>([])
const perStepStart = ref<number[]>([])
const remainingTime = computed(() => {
	if (!mission.value) return 0
	return Math.max((mission.value.totalTime || 0) - currentTime.value, 0)
})

// Add currentUser for Header
import type { User } from '../services/database'
const currentUser = ref<User | null>(null)
onMounted(() => {
	const userStr = sessionStorage.getItem('currentUser')
	if (userStr) currentUser.value = JSON.parse(userStr)
})
// Wake lock for keeping screen on during mission
let wakeLock: any = null

async function requestWakeLock() {
	try {
		if ('wakeLock' in navigator) {
			wakeLock = await (navigator as any).wakeLock.request('screen')
			console.log('Wake lock acquired')
		}
	} catch (err) {
		console.warn('Failed to acquire wake lock:', err)
	}
}

function releaseWakeLock() {
	if (wakeLock) {
		try {
			wakeLock.release()
			wakeLock = null
			console.log('Wake lock released')
		} catch (err) {
			console.warn('Failed to release wake lock:', err)
		}
	}
}

// Re-acquire wake lock when page becomes visible again
function handleVisibilityChange() {
	if (document.visibilityState === 'visible' && isRunning.value && !wakeLock) {
		requestWakeLock()
	}
}

// Speech synthesis helpers and variables
let speechIntervalId: number | null = null
const speechModeIndex = ref(0) // 0=mission, 1=step, 2=remaining

// Mission voice selection (set on Missions page)
// - 'default' => browser speechSynthesis
// - otherwise => ElevenLabs voice_id
const missionVoice = ref<string>('default')

onMounted(() => {
	const stored = sessionStorage.getItem('missionVoice')
	if (stored) missionVoice.value = stored
})

// ElevenLabs audio playback state
let ttsAudio: HTMLAudioElement | null = null
let ttsAudioUrl: string | null = null
let ttsAbort: AbortController | null = null

// TTS pre-loading cache
const ttsCache = new Map<string, { blob: Blob, timestamp: number }>()
const TTS_CACHE_MAX_AGE = 30000 // 30 seconds

// Ensure only one voice plays at a time (no overlap)
let speakChain: Promise<void> = Promise.resolve()
let speakToken = 0

function stopTtsAudio() {
	if (ttsAbort) {
		try { ttsAbort.abort() } catch (e) { /* ignore */ }
		ttsAbort = null
	}
	if (ttsAudio) {
		try { ttsAudio.pause() } catch (e) { /* ignore */ }
		ttsAudio = null
	}
	if (ttsAudioUrl) {
		try { URL.revokeObjectURL(ttsAudioUrl) } catch (e) { /* ignore */ }
		ttsAudioUrl = null
	}
}

function cancelBrowserSpeech() {
	try {
		if (typeof window === 'undefined' || !(window as any).speechSynthesis) return
		const synth = (window as any).speechSynthesis
		synth.cancel()
	} catch (e) {
		// ignore
	}
}

function stopAllSpeech() {
	speakToken++
	stopTtsAudio()
	cancelBrowserSpeech()
	// reset queue so the next speak starts immediately
	speakChain = Promise.resolve()
}

function waitForAudioEnded(audio: HTMLAudioElement): Promise<void> {
	return new Promise((resolve) => {
		const done = () => resolve()
		audio.onended = done
		audio.onerror = done
	})
}

function waitForUtterance(utter: SpeechSynthesisUtterance): Promise<void> {
	return new Promise((resolve) => {
		utter.onend = () => resolve()
		utter.onerror = () => resolve()
	})
}

async function prefetchTts(text: string, voiceId: string): Promise<void> {
	const cacheKey = `${voiceId}:${text}`
	
	// Clean old cache entries
	const now = Date.now()
	for (const [key, value] of ttsCache.entries()) {
		if (now - value.timestamp > TTS_CACHE_MAX_AGE) {
			ttsCache.delete(key)
		}
	}
	
	// Skip if already cached
	if (ttsCache.has(cacheKey)) {
		return
	}
	
	try {
		const res = await fetch(`${API_BASE_URL}/api/tts`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				text,
				voice_id: voiceId,
				format: 'mp3'
			})
		})
		
		if (!res.ok) return
		
		const buf = await res.arrayBuffer()
		const blob = new Blob([buf], { type: res.headers.get('content-type') || 'audio/mpeg' })
		
		ttsCache.set(cacheKey, { blob, timestamp: Date.now() })
		console.log(`Pre-fetched TTS for: "${text.substring(0, 30)}..."`)
	} catch (e) {
		console.warn('TTS pre-fetch failed', e)
	}
}

function getNextAnnouncementText(): string | null {
	if (!mission.value) return null
	const mode = (speechModeIndex.value + 1) % 4
	
	if (mode === 0) {
		return `mission is ${mission.value.name}.`
	} else if (mode === 1) {
		const stepName = mission.value.steps[currentStepIndex.value]?.name || ''
		if (stepName) {
			return `step is ${stepName}.`
		}
	} else if (mode === 2) {
		const rem = remainingTime.value
		const remMin = Math.floor(rem / 60)
		const remSec = rem % 60
		return `Total remaining time is ${remMin} minutes ${remSec} seconds.`
	} else if (mode === 3) {
		const stepName = mission.value.steps[currentStepIndex.value]?.name || ''
		if (stepName) {
			return `are you done with ${stepName}? hit the done button when you are.`
		}
	}
	return null
}

async function performTts(text: string, token: number): Promise<void> {
	const voiceId = missionVoice.value
	if (!voiceId || voiceId === 'default') return
	if (token !== speakToken) return

	const startTime = Date.now()
	const cacheKey = `${voiceId}:${text}`
	const abort = new AbortController()
	ttsAbort = abort

	try {
		let blob: Blob
		
		// Check cache first
		const cached = ttsCache.get(cacheKey)
		if (cached) {
			console.log(`Using cached TTS for: "${text.substring(0, 30)}..."`)
			blob = cached.blob
			ttsCache.delete(cacheKey) // Remove from cache after use
		} else {
			const fetchStart = Date.now()
			const res = await fetch(`${API_BASE_URL}/api/tts`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				signal: abort.signal,
				body: JSON.stringify({
					text,
					voice_id: voiceId,
					format: 'mp3'
				})
			})
			console.log(`TTS fetch completed in ${Date.now() - fetchStart}ms`)

			if (token !== speakToken) return

			if (!res.ok) {
				const msg = await res.text().catch(() => '')
				throw new Error(msg || `TTS failed (${res.status})`)
			}

			const buf = await res.arrayBuffer()
			if (token !== speakToken) return

			blob = new Blob([buf], { type: res.headers.get('content-type') || 'audio/mpeg' })
		}

		if (ttsAudioUrl) URL.revokeObjectURL(ttsAudioUrl)
		ttsAudioUrl = URL.createObjectURL(blob)
		ttsAudio = new Audio(ttsAudioUrl)

		try {
			const playStart = Date.now()
			await ttsAudio.play()
			await waitForAudioEnded(ttsAudio)
			console.log(`TTS playback completed in ${Date.now() - playStart}ms`)
			console.log(`TTS total time: ${Date.now() - startTime}ms`)
			
			// Pre-fetch next announcement if we're in a mission
			if (isRunning.value && voiceId !== 'default') {
				const nextText = getNextAnnouncementText()
				if (nextText) {
					prefetchTts(nextText, voiceId).catch(() => {})
				}
			}
		} catch (e) {
			// play() can throw if user hasn't interacted
			console.warn('TTS play() failed', e)
			return
		}
	} catch (e) {
		if ((e as any)?.name !== 'AbortError') {
			console.warn('TTS playback failed', e)
		}
	} finally {
		if (ttsAbort === abort) ttsAbort = null
	}
}

async function performBrowserSpeech(text: string, token: number): Promise<void> {
	if (token !== speakToken) return
	if (typeof window === 'undefined' || !(window as any).speechSynthesis) return
	const synth = (window as any).speechSynthesis
	try { synth.cancel() } catch (e) { /* ignore */ }

	const utter = new SpeechSynthesisUtterance(text)
	synth.speak(utter)
	await waitForUtterance(utter)
}

function speak(text: string) {
	const cleaned = (text || '').trim()
	if (!cleaned) return
	const token = speakToken
	speakChain = speakChain
		.then(async () => {
			if (token !== speakToken) return
			if (missionVoice.value && missionVoice.value !== 'default') {
				await performTts(cleaned, token)
			} else {
				await performBrowserSpeech(cleaned, token)
			}
		})
		.catch(() => { /* swallow */ })
}

function speakImmediate(text: string) {
	const cleaned = (text || '').trim()
	if (!cleaned) return
	stopAllSpeech()
	const token = speakToken
	speakChain = speakChain
		.then(async () => {
			if (token !== speakToken) return
			if (missionVoice.value && missionVoice.value !== 'default') {
				await performTts(cleaned, token)
			} else {
				await performBrowserSpeech(cleaned, token)
			}
		})
		.catch(() => { /* swallow */ })
}

function announceTick() {
	if (!mission.value) return
	const mode = speechModeIndex.value % 4
	if (mode === 0) {
		const text = `mission is ${mission.value.name}.`
		speak(text)
	} else if (mode === 1) {
		const stepName = mission.value.steps[currentStepIndex.value]?.name || ''
		if (stepName) {
			speak(`step is ${stepName}.`)
		}
	} else if (mode === 2) {
		const rem = remainingTime.value
		const remMin = Math.floor(rem / 60)
		const remSec = rem % 60
		speak(`Total remaining time is ${remMin} minutes ${remSec} seconds.`)
	} else if (mode === 3) {
		const stepName = mission.value.steps[currentStepIndex.value]?.name || ''
		if (stepName) {
			speak(`are you done with ${stepName}? hit the done button when you are.`)
		}
	}
	speechModeIndex.value = (speechModeIndex.value + 1) % 4
}

function startSpeechLoop() {
	stopSpeechLoop()
	speechModeIndex.value = 0
	
	// Pre-fetch the first announcement
	if (missionVoice.value && missionVoice.value !== 'default') {
		const firstText = getNextAnnouncementText()
		if (firstText) {
			prefetchTts(firstText, missionVoice.value).catch(() => {})
		}
	}
	
	speechIntervalId = setInterval(() => {
		if (isRunning.value) announceTick()
	}, 10000) as unknown as number
}

function stopSpeechLoop() {
	if (speechIntervalId) {
		clearInterval(speechIntervalId)
		speechIntervalId = null
	}
	stopAllSpeech()
	// Clear cache when stopping
	ttsCache.clear()
}

watch(currentStepIndex, (newIndex, oldIndex) => {
	if (!mission.value) return
	if (newIndex === oldIndex) return
	if (isRunning.value) {
		const name = mission.value.steps[newIndex]?.name || ''
		if (name) speakImmediate(`Starting step ${name}.`)
	}
})
const progressPercentage = computed(() => {
	if (!mission.value || !mission.value.totalTime) return 0
	return Math.min(100, (currentTime.value / mission.value.totalTime) * 100)
})

const formatTime = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60)
	const secs = seconds % 60
	return `${minutes}m ${secs}s`
}

const loadMission = async () => {
	loading.value = true
	try {
		const id = route.params.id as string
		const rawMission = await dbService.getMission(id)
		if (rawMission) {
			// Parse steps and totalTime from rawMission.data if present
			let steps = []
			let totalTime = 0
			if (rawMission.data) {
				try {
					const parsed = typeof rawMission.data === 'string' ? JSON.parse(rawMission.data) : rawMission.data
					steps = parsed.steps || []
					totalTime = parsed.totalTime || 0
				} catch (e) {
					steps = []
					totalTime = 0
				}
			}
			mission.value = {
				...rawMission,
				steps,
				totalTime
			}
			perStepElapsed.value = Array(steps.length).fill(0)
		} else {
			mission.value = null
		}
	} catch (error) {
		mission.value = null
	} finally {
		loading.value = false
	}
}

const startMission = () => {
	if (!mission.value) return
	
	requestWakeLock()
	isRunning.value = true
	isCompleted.value = false
	perStepStart.value = Array(mission.value.steps.length).fill(null)
	perStepStart.value[0] = Date.now()
	
	// Announce starting mission and step immediately
	speakImmediate(`Starting mission ${mission.value.name}. Starting step ${mission.value.steps[0]?.name || ''}.`)
	
	// Start speech loop after a short delay to avoid conflicts
	setTimeout(() => {
		if (isRunning.value) {
			startSpeechLoop()
		}
	}, 100)
	
	intervalId = window.setInterval(() => {
		currentTime.value++
		if (Array.isArray(perStepElapsed.value) && typeof perStepElapsed.value?.[currentStepIndex.value] === 'number') {
			perStepElapsed.value[currentStepIndex.value]!++
		}
		if (currentTime.value >= (mission.value?.totalTime || 0)) {
			completeMission()
		}
	}, 1000)
}

const markStepDone = () => {
	if (!mission.value) return
	if (currentStepIndex.value < mission.value.steps.length - 1) {
		// Record start time for next step
		perStepStart.value[currentStepIndex.value + 1] = Date.now()
		currentStepIndex.value++
	} else {
		completeMission()
	}
}

const resumeMission = () => {
	requestWakeLock()
	startSpeechLoop()
	if (!mission.value) return
	isRunning.value = true
		intervalId = window.setInterval(() => {
			currentTime.value++
			if (Array.isArray(perStepElapsed.value) && typeof perStepElapsed.value?.[currentStepIndex.value] === 'number') {
				perStepElapsed.value[currentStepIndex.value]!++
			}
			if (currentTime.value >= (mission.value?.totalTime || 0)) {
				completeMission()
			}
		}, 1000)
}

const completeMission = async () => {
	// final summary announcement
	if (mission.value) {
		const total = mission.value.totalTime || 0
		const remaining = total - currentTime.value
		let timingText = ''
		if (remaining > 0) {
			const m = Math.floor(remaining / 60)
			const s = remaining % 60
			timingText = `You finished ${m} minutes ${s} seconds early.`
		} else if (remaining < 0) {
			const late = Math.abs(remaining)
			const m = Math.floor(late / 60)
			const s = late % 60
			timingText = `You were ${m} minutes ${s} seconds late.`
		} else {
			timingText = `You finished exactly on time.`
		}
	speakImmediate(`Mission ${mission.value.name} is complete. ${timingText}`)

		// Record mission completion activity
		if (currentUser.value?.id) {
			try {
				await dbService.addUserActivity(currentUser.value.id, 'mission', new Date().toISOString(), {
					missionId: mission.value?.id,
					missionName: (mission.value as any)?.title || (mission.value as any)?.name || '',
					elapsedSeconds: currentTime.value,
					totalSeconds: mission.value?.totalTime || 0,
					steps: (mission.value?.steps || []).map((s: any, idx: number) => ({
						index: idx,
						name: s?.name || s?.title || `Step ${idx + 1}`,
						elapsedSeconds: perStepElapsed.value?.[idx] ?? null
					}))
				})
				console.log('Mission activity recorded')
			} catch (err) {
				console.error('Failed to record mission activity:', err)
			}
		}
	}
	isRunning.value = false
	releaseWakeLock()
	if (intervalId) {
		clearInterval(intervalId)
		intervalId = null
	}
}

onMounted(() => {
	loadMission()
	document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
	stopSpeechLoop()
	releaseWakeLock()
	if (intervalId) {
		clearInterval(intervalId)
		intervalId = null
	}
	document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
.mission-runner-container {
		min-height: 100vh;
		background-color: #f5f5f5;
			padding-bottom: 12rem;
}
.page-header {
	background: white;
	padding: 1rem 2rem; 
	border-bottom: 1px solid #ddd;
	display: flex;
	justify-content: space-between;
	align-items: center;
}
.page-header h1 {
	margin: 0;
	color: #333;
}
.mission-info {
	text-align: center;
}
.current-time {
	font-size: 4rem;
	font-weight: bold;
	color: #222;
	margin-top: 1rem;
}
.runner-controls {
	margin-bottom: 2rem;
	text-align: center;
}
.timer-display {
	margin-bottom: 1rem;
}
.progress-bar {
	width: 100%;
	height: 16px;
	background: #eee;
	border-radius: 8px;
	overflow: hidden;
	margin-bottom: 1rem;
}
.progress-fill {
	height: 100%;
	background: #2d8f2d;
	transition: width 0.3s;
}
.current-step-display {
	margin-bottom: 1rem;
}
.current-step-name {
	font-size: 1.3rem;
	font-weight: 600;
	color: #333;
}
.current-step-elapsed {
	font-size: 1rem;
	color: #666;
}
.steps-section {
	margin-top: 2rem;
}
.steps-list {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	max-height: 60vh;
	overflow-y: auto;
}
.step-item {
	display: flex;
	align-items: center;
	background: white;
	border-radius: 6px;
	box-shadow: 0 2px 8px rgba(0,0,0,0.04);
	padding: 1rem 2rem;
	gap: 1.5rem;
}
.step-number {
	font-size: 1.2rem;
	font-weight: bold;
	color: #2d8f2d;
	width: 32px;
	text-align: center;
}
.step-content {
	flex: 1;
}
.step-content h4 {
	margin: 0 0 0.5rem 0;
	font-size: 1.1rem;
	color: #222;
}
.step-elapsed {
	font-size: 0.95rem;
	color: #666;
}
.step-status {
	width: 100px;
	text-align: right;
}
.status-completed {
	color: #2d8f2d;
	font-weight: bold;
}
.status-current {
	color: #666;
	font-weight: bold;
}
.status-upcoming {
	color: #aaa;
}
.no-steps {
	text-align: center;
	color: #888;
	font-size: 1.1rem;
	margin-top: 1rem;
}
.completion-message {
	text-align: center;
	margin-top: 2rem;
	color: #2d8f2d;
}
.loading {
	text-align: center;
	color: #888;
	font-size: 1.2rem;
	margin-top: 2rem;
}
.error {
	text-align: center;
	color: #c0392b;
	font-size: 1.2rem;
	margin-top: 2rem;
}
.action-btn {
	width: 100px;
	height: 100px;
	padding: 0;
	font-size: 1.1rem;
	border-radius: 8px;
	border: none;
	cursor: pointer;
	transition: all 0.2s ease;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	display: flex;
	align-items: center;
	justify-content: center;
}
.action-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}
</style>